import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const YEARS = Array.from({ length: 90 }, (_, i) => 2015 - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function Profile() {
  const { t } = useTranslation();
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    gender: "",
    birth_year: "",
    birth_month: "",
    birth_day: "",
    birth_hour: "",
    bank_holder: "",
    bank_name_agent: "",
    bank_number_agent: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        gender: user.gender || "",
        birth_year: user.birth_year || "",
        birth_month: user.birth_month || "",
        birth_day: user.birth_day || "",
        birth_hour: user.birth_hour ?? "",
        bank_holder: user.bank_holder || "",
        bank_name_agent: user.bank_name_agent || "",
        bank_number_agent: user.bank_number_agent || "",
      });
    }
  }, [user]);

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        if (["birth_year", "birth_month", "birth_day", "birth_hour"].includes(k)) {
          payload[k] = Number(v);
        } else {
          payload[k] = v;
        }
      });
      await updateProfile(payload);
      toast.success(t("profile.updated"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16" data-testid="profile-page">
      <h1 className="font-serif-cn text-4xl text-[#003645] font-semibold tracking-tight">
        {t("profile.title")}
      </h1>

      <div className="mt-4 inline-flex items-center gap-2 text-sm">
        {user.profile_complete ? (
          <><CheckCircle2 size={14} className="text-[#00868e]" /><span className="text-[#00868e]">{t("profile.complete")}</span></>
        ) : (
          <><AlertCircle size={14} className="text-amber-600" /><span className="text-amber-700">{t("profile.incomplete")}</span></>
        )}
      </div>

      {user.role === "agent" && user.agent_status === "pending" && (
        <div
          className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid="agent-pending-notice"
        >
          {t("auth.agentPending")}
        </div>
      )}

      <form onSubmit={submit} className="mt-8 rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.name")}</label>
            <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.phone")}</label>
            <input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.email")}</label>
            <input className={inputClass} disabled value={user.email} />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("profile.gender")}</label>
            <select
              className={inputClass}
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              data-testid="profile-gender-select"
            >
              <option value="">—</option>
              <option value="male">{t("profile.male")}</option>
              <option value="female">{t("profile.female")}</option>
              <option value="other">{t("profile.other")}</option>
            </select>
          </div>
        </div>

        {user.role === "agent" && (
          <div className="pt-4 border-t border-[#e5ebeb]" data-testid="agent-bank-section">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold mb-3">
              {t("agentHub.bankInfo")}
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-[#4a6e77] mb-1.5">{t("agentHub.bankHolder")}</label>
                <input className={inputClass} value={form.bank_holder} onChange={(e) => setForm({ ...form, bank_holder: e.target.value })} data-testid="agent-bank-holder" />
              </div>
              <div>
                <label className="block text-xs text-[#4a6e77] mb-1.5">{t("agentHub.bankName")}</label>
                <input className={inputClass} value={form.bank_name_agent} onChange={(e) => setForm({ ...form, bank_name_agent: e.target.value })} data-testid="agent-bank-name" />
              </div>
              <div>
                <label className="block text-xs text-[#4a6e77] mb-1.5">{t("agentHub.bankNumber")}</label>
                <input className={inputClass} value={form.bank_number_agent} onChange={(e) => setForm({ ...form, bank_number_agent: e.target.value })} data-testid="agent-bank-number" />
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="text-xs text-[#4a6e77] mb-2">{t("profile.birthDate")}</div>
          <div className="grid grid-cols-4 gap-2">
            <select className={inputClass} value={form.birth_year} onChange={(e) => setForm({ ...form, birth_year: e.target.value })} data-testid="profile-birth-year">
              <option value="">{t("profile.birthYear")}</option>
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className={inputClass} value={form.birth_month} onChange={(e) => setForm({ ...form, birth_month: e.target.value })} data-testid="profile-birth-month">
              <option value="">{t("profile.birthMonth")}</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select className={inputClass} value={form.birth_day} onChange={(e) => setForm({ ...form, birth_day: e.target.value })} data-testid="profile-birth-day">
              <option value="">{t("profile.birthDay")}</option>
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select className={inputClass} value={form.birth_hour} onChange={(e) => setForm({ ...form, birth_hour: e.target.value })} data-testid="profile-birth-hour">
              <option value="">{t("profile.birthHour")}</option>
              {HOURS.map((h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
            </select>
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60"
            data-testid="profile-save-btn"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {t("common.save")}
          </button>
        </div>
      </form>
    </div>
  );
}
